// ###### IMPORTS
import { createContext, useContext, useState, useEffect } from 'react';

// ###### CONTEXT CREATION
const DeviceContext = createContext();

// ###### DEVICE DETECTION UTILITIES
const getDeviceType = (screenWidth, userAgent) => {
  if (screenWidth < 768) return 'mobile';
  if (screenWidth < 1024) return 'tablet';
  return 'desktop';
};

const getScreenSize = (screenWidth) => {
  if (screenWidth < 640) return 'sm';
  if (screenWidth < 768) return 'md';
  if (screenWidth < 1024) return 'lg';
  if (screenWidth < 1280) return 'xl';
  return '2xl';
};

const detectDevice = () => {
  if (typeof window === 'undefined') {
    return {
      isMobile: false,
      isTablet: false,
      isDesktop: true,
      deviceType: 'desktop',
      touchCapable: false,
      screenSize: 'lg',
      viewportHeight: 0,
      realViewportHeight: 0
    };
  }

  const userAgent = navigator.userAgent;
  const screenWidth = window.innerWidth;
  const screenHeight = window.innerHeight;
  
  const isMobile = /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent) || screenWidth < 768;
  const isTablet = /iPad|Android/i.test(userAgent) || (screenWidth >= 768 && screenWidth < 1024);
  const isDesktop = screenWidth >= 1024;
  const touchCapable = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  
  return {
    isMobile,
    isTablet,
    isDesktop,
    deviceType: getDeviceType(screenWidth, userAgent),
    touchCapable,
    screenSize: getScreenSize(screenWidth),
    viewportHeight: screenHeight,
    realViewportHeight: screenHeight * 0.01
  };
};

// ###### DEVICE PROVIDER COMPONENT
export const DeviceProvider = ({ children }) => {
  const [deviceInfo, setDeviceInfo] = useState(detectDevice());

  // ###### DEVICE DETECTION EFFECT
  useEffect(() => {
    const updateDeviceInfo = () => {
      setDeviceInfo(detectDevice());
    };

    updateDeviceInfo();
    window.addEventListener('resize', updateDeviceInfo);
    
    return () => window.removeEventListener('resize', updateDeviceInfo);
  }, []);

  // ###### VIEWPORT HEIGHT MANAGEMENT (integrated from useViewportHeight)
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    const setRealVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    setRealVH();
    window.addEventListener('resize', setRealVH);

    return () => window.removeEventListener('resize', setRealVH);
  }, []);

  return (
    <DeviceContext.Provider value={deviceInfo}>
      {children}
    </DeviceContext.Provider>
  );
};

// ###### CUSTOM HOOKS
export const useDevice = () => {
  const context = useContext(DeviceContext);
  if (!context) {
    throw new Error('useDevice must be used within DeviceProvider');
  }
  return context;
};

export const useViewport = () => {
  const { viewportHeight, realViewportHeight } = useDevice();
  return { viewportHeight, realViewportHeight };
};
